import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Crown, CheckCircle2, Clock, XCircle, Zap, ShieldCheck, ClipboardList, Gift, Wallet, Copy, Users, Send, Award, Trophy } from "lucide-react";
import { api, apiError } from "../lib/api";
import { rupiah } from "../lib/format";
import { AuthImage } from "../components/AuthImage";
import { useAuth } from "../context/AuthContext";

export default function Member() {
  const { refresh } = useAuth();
  const [m, setM] = useState(null);
  const [plans, setPlans] = useState([]);
  const [bank, setBank] = useState({ bank_name: "", bank_account: "", bank_owner: "" });
  const [sel, setSel] = useState("");
  const [proof, setProof] = useState("");
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [quote, setQuote] = useState(null);
  // referral (apply side)
  const [refCode, setRefCode] = useState((localStorage.getItem("pending_ref") || "").toUpperCase());
  const [refValid, setRefValid] = useState(null); // {referrer_name, discount}
  const [refChecking, setRefChecking] = useState(false);
  // referral dashboard
  const [ref, setRef] = useState(null);
  const [board, setBoard] = useState(null);
  const [wd, setWd] = useState({ amount: "", bank_name: "", bank_account: "", bank_owner: "" });
  const [wdSaving, setWdSaving] = useState(false);

  const load = () => api.get("/membership/me").then((r) => setM(r.data));
  const loadRef = () => {
    api.get("/referral/me").then((r) => setRef(r.data)).catch(() => {});
    api.get("/referral/leaderboard").then((r) => setBoard(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    loadRef();
    api.get("/membership/plans").then((r) => { setPlans(r.data.plans); setSel(r.data.plans[0]?.key || ""); });
    api.get("/settings/bank").then((r) => setBank(r.data));
  }, []);

  const onFile = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try { const { data } = await api.post("/uploads", fd); setProof(data.path); toast.success("Bukti transfer terunggah"); }
    catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const checkRef = async () => {
    if (!refCode.trim()) return;
    setRefChecking(true);
    try {
      const { data } = await api.post("/referral/validate", { code: refCode.trim() });
      setRefValid(data);
      toast.success(`Kode valid! Diskon ${rupiah(data.discount)}`);
    } catch (e) { setRefValid(null); toast.error(apiError(e)); }
    finally { setRefChecking(false); }
  };

  const discount = refValid?.discount || 0;

  useEffect(() => {
    if (!sel || !m || m.status === "pending") return;
    api.post("/membership/quote", { plan_key: sel, referral_code: refValid ? refCode.trim() : "" })
      .then((r) => setQuote(r.data)).catch(() => {});
  }, [sel, refValid, m]); // eslint-disable-line

  const submit = async () => {
    if (!sel) { toast.error("Pilih paket dulu"); return; }
    if (!proof) { toast.error("Unggah bukti transfer dulu"); return; }
    setSaving(true);
    try {
      await api.post("/membership/apply", { payment_proof: proof, plan_key: sel, note, referral_code: refValid ? refCode.trim() : "" });
      localStorage.removeItem("pending_ref");
      toast.success("Pengajuan terkirim! Menunggu verifikasi admin.");
      await refresh();
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const copyLink = () => {
    const link = `${window.location.origin}/register?ref=${ref.referral_code}`;
    navigator.clipboard.writeText(link);
    toast.success("Link referral disalin!");
  };

  const submitWithdraw = async () => {
    const amt = parseInt(wd.amount) || 0;
    if (amt < (ref?.min_withdrawal || 0)) { toast.error(`Minimum penarikan ${rupiah(ref?.min_withdrawal || 0)}`); return; }
    if (amt > (ref?.balance || 0)) { toast.error("Saldo tidak cukup"); return; }
    if (!wd.bank_name || !wd.bank_account || !wd.bank_owner) { toast.error("Lengkapi rekening tujuan"); return; }
    setWdSaving(true);
    try {
      await api.post("/withdrawals", { amount: amt, bank_name: wd.bank_name, bank_account: wd.bank_account, bank_owner: wd.bank_owner });
      toast.success("Permintaan penarikan terkirim. Diproses maks. 2×24 jam (kecuali Minggu).");
      setWd({ amount: "", bank_name: "", bank_account: "", bank_owner: "" });
      await refresh();
      loadRef();
    } catch (e) { toast.error(apiError(e)); } finally { setWdSaving(false); }
  };

  if (!m) return <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-emerald-600" size={30} /></div>;
  const fmt = (d) => d ? new Date(d).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" }) : "-";
  const WD_LABEL = { PENDING: "Diproses", COMPLETED: "Selesai", REJECTED: "Ditolak" };
  const BADGE_STYLE = {
    none: { ring: "border-stone-200", bg: "bg-stone-50", text: "text-stone-400", icon: "text-stone-300" },
    bronze: { ring: "border-amber-300", bg: "bg-amber-50", text: "text-amber-700", icon: "text-amber-600" },
    silver: { ring: "border-slate-300", bg: "bg-slate-50", text: "text-slate-600", icon: "text-slate-500" },
    gold: { ring: "border-yellow-400", bg: "bg-yellow-50", text: "text-yellow-700", icon: "text-yellow-500" },
    platinum: { ring: "border-emerald-400", bg: "bg-gradient-to-br from-emerald-50 to-sky-50", text: "text-emerald-700", icon: "text-emerald-600" },
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <div className="text-center mb-6">
        <Crown className="mx-auto text-amber-500" size={36} />
        <h1 className="font-display text-3xl font-bold text-stone-900 mt-2">Keanggotaan Member</h1>
        <p className="text-stone-500 text-sm mt-1">Member bisa lihat <b>harga lengkap</b> & <b>mengajukan produk</b> untuk dicek admin ke supplier.</p>
      </div>

      {m.is_member ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center" data-testid="member-active">
          <CheckCircle2 className="mx-auto text-emerald-600" size={40} />
          <p className="font-display font-bold text-lg text-emerald-800 mt-2">Akses Lihat Harga Aktif ✅</p>
          <p className="text-sm text-emerald-700 mt-1">Berlaku sampai <b>{fmt(m.expires_at)}</b>.</p>
        </div>
      ) : null}

      <div className="bg-white border border-stone-200 rounded-2xl p-5 my-5 grid sm:grid-cols-2 gap-4" data-testid="member-status">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${m.is_member ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-400"}`}><ShieldCheck size={22} /></div>
          <div>
            <p className="text-xs text-stone-500">Akses Lihat Harga</p>
            {m.is_member ? <p className="font-semibold text-emerald-700 text-sm">Aktif s/d {fmt(m.expires_at)}</p> : <p className="font-semibold text-stone-500 text-sm">Belum aktif</p>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center"><ClipboardList size={22} /></div>
          <div>
            <p className="text-xs text-stone-500">Kuota Pengajuan Pembelian</p>
            <p className="font-semibold text-amber-700 text-sm" data-testid="member-credits">{m.request_credits || 0}× tersisa</p>
          </div>
        </div>
      </div>

      {m.status === "pending" ? (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center" data-testid="member-pending">
          <Clock className="mx-auto text-amber-600" size={40} />
          <p className="font-display font-bold text-lg text-amber-800 mt-2">Menunggu Verifikasi Admin</p>
          <p className="text-sm text-amber-700 mt-1">Bukti transfermu sedang dicek. Akses terbuka otomatis setelah disetujui.</p>
          {m.amount_due && <p className="text-sm text-amber-800 mt-2 bg-white/60 rounded-lg py-2">Nominal dibayar: <b>{rupiah(m.amount_due)}</b> · kode unik <b>{m.unique_code}</b></p>}
          {m.payment_proof && <AuthImage path={m.payment_proof} className="mx-auto mt-4 w-40 rounded-lg border border-amber-200" />}
        </div>
      ) : (
        <div className="space-y-5">
          {m.status === "rejected" && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-sm text-red-700" data-testid="member-rejected">
              <XCircle size={18} /> Pengajuan sebelumnya ditolak. Silakan ajukan ulang.
            </div>
          )}

          {/* Referral code input */}
          <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="member-referral-input">
            <p className="font-semibold text-stone-800 flex items-center gap-2 mb-1"><Gift size={18} className="text-amber-500" />Punya kode referral?</p>
            <p className="text-xs text-stone-500 mb-3">Masukkan kode dari temanmu, dapat <b>diskon Rp10.000</b> untuk semua paket. Contoh: paket Rp50.000 → cukup bayar <b>Rp40.000</b>.</p>
            <div className="flex gap-2">
              <input data-testid="member-refcode" value={refCode} onChange={(e) => { setRefCode(e.target.value.toUpperCase()); setRefValid(null); }} placeholder="Kode referral (opsional)" className="flex-1 border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400 uppercase" />
              <button data-testid="member-refcheck" onClick={checkRef} disabled={refChecking || !refCode.trim()} className="bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold px-4 rounded-lg disabled:opacity-50 flex items-center gap-1.5">{refChecking && <Loader2 className="animate-spin" size={14} />}Cek</button>
            </div>
            {refValid && <p className="text-xs text-emerald-700 mt-2" data-testid="member-refvalid">✓ Kode dari <b>{refValid.referrer_name}</b> valid. Potongan <b>{rupiah(refValid.discount)}</b> sudah dipotong dari harga paket di bawah.</p>}
          </div>

          {["access", "purchase"].map((t) => {
            const list = plans.filter((p) => p.type === t);
            if (!list.length) return null;
            return (
              <div key={t}>
                <p className="font-display font-bold text-stone-800 mb-1">{t === "access" ? "Paket Akses Lihat Harga" : "Paket Pengajuan Pembelian"}</p>
                <p className="text-xs text-stone-500 mb-3">{t === "access" ? "Buka semua harga tanpa sensor." : "Tim kami di China cek pabrik & harga asli tiap pengajuan — kuota diakumulasi."}</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {list.map((p) => {
                    const isBuy = p.type === "purchase";
                    const active = sel === p.key;
                    const finalPrice = Math.max(0, p.price - discount);
                    return (
                      <button key={p.key} data-testid={`member-plan-${p.key}`} onClick={() => setSel(p.key)}
                        className={`text-left rounded-2xl border-2 p-4 transition ${active ? (isBuy ? "border-amber-500 bg-amber-50" : "border-emerald-600 bg-emerald-50") : "border-stone-200 bg-white hover:border-stone-300"}`}>
                        <div className="flex items-center gap-1.5 mb-1">
                          {isBuy ? <ClipboardList size={16} className="text-amber-500" /> : <ShieldCheck size={16} className="text-emerald-600" />}
                          <span className="font-display font-bold text-stone-900">{p.name}</span>
                        </div>
                        {discount > 0 ? (
                          <div>
                            <p className="text-sm text-stone-400 line-through">{rupiah(p.price)}</p>
                            <p className="text-2xl font-display font-extrabold text-emerald-700" data-testid={`member-price-${p.key}`}>{rupiah(finalPrice)}</p>
                          </div>
                        ) : (
                          <p className="text-2xl font-display font-extrabold text-stone-900" data-testid={`member-price-${p.key}`}>{rupiah(p.price)}</p>
                        )}
                        <p className="text-xs text-stone-500 mt-1">{isBuy ? <><b>{p.requests}×</b> pengajuan pembelian</> : <>{p.days} hari akses lihat harga</>}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 text-xs text-stone-600 leading-relaxed">
            <b>Kenapa pengajuan berbayar?</b> Karena tim kami di China turun langsung mengecek pabriknya asli atau tidak dan apakah harganya benar — jadi kamu tidak asal order coba-coba. Paket akses hanya untuk melihat harga tanpa sensor.
          </div>

          <div className="bg-white border border-stone-200 rounded-2xl p-5">
            <p className="font-semibold text-stone-800 mb-3">1. Bayar via QRIS</p>
            <div className="flex flex-col items-center">
              <img src="/qris-kedaiimpor.jpg" alt="QRIS Kedai Impor" data-testid="member-qris" className="w-56 rounded-xl border border-stone-200" />
              <p className="text-xs text-stone-400 mt-2 text-center">QRIS a.n. <b>KEDAI IMPOR</b> · scan pakai DANA / OVO / GoPay / m-banking</p>
            </div>
            {quote && (
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-center" data-testid="member-amount-box">
                <p className="text-xs text-stone-500">Bayar PERSIS sejumlah ini (ada 3 angka unik di belakang):</p>
                <p className="text-3xl font-display font-extrabold text-amber-700" data-testid="member-amount-due">{rupiah(quote.amount_due)}</p>
                <p className="text-xs text-stone-500 mt-1">Harga paket {rupiah(quote.net_price)}{quote.discount > 0 ? " (sudah diskon referral)" : ""} + kode unik <b className="text-amber-700">{quote.unique_code}</b></p>
                <p className="text-[11px] text-emerald-700 mt-2">⚠️ Nominal harus PERSIS sampai angka unik, agar admin mudah mencocokkan pembayaranmu di mutasi.</p>
              </div>
            )}
          </div>

          <div className="bg-white border border-stone-200 rounded-2xl p-5 space-y-3">
            <p className="font-semibold text-stone-800">2. Kirim bukti transfer</p>
            <label className="flex items-center gap-2 border border-dashed border-stone-300 rounded-lg px-3 py-2.5 text-sm text-stone-500 cursor-pointer hover:border-emerald-400">
              {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
              {proof ? "Ganti bukti transfer" : "Unggah foto bukti transfer"}
              <input data-testid="member-proof" type="file" accept="image/*" onChange={onFile} className="hidden" />
            </label>
            {proof && <AuthImage path={proof} className="w-40 rounded-lg border border-stone-200" />}
            <textarea data-testid="member-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Catatan (opsional): nama pengirim / jam transfer" className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400 resize-none" />
            <button data-testid="member-submit" onClick={submit} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="animate-spin" size={16} />}Kirim Pengajuan Member
            </button>
            <p className="text-xs text-stone-400 text-center">Admin akan cek & setujui jika uang sudah diterima.</p>
          </div>
        </div>
      )}

      {/* Referral & Wallet dashboard (members only) */}
      {m.is_member && ref && ref.referral_code && (
        <div className="mt-8 space-y-5" data-testid="member-referral-dashboard">
          <div className="bg-gradient-to-br from-amber-50 to-emerald-50 border border-amber-200 rounded-2xl p-6">
            <p className="font-display font-bold text-lg text-stone-900 flex items-center gap-2"><Gift size={20} className="text-amber-500" />Program Ajak Teman</p>
            <p className="text-sm text-stone-600 mt-1">Tiap ajak teman jadi member pakai kodemu: kamu dapat <b>{rupiah(ref.fee)}</b> ke saldo, teman dapat diskon <b>{rupiah(ref.discount)}</b>. Contoh: paket {rupiah(50000)} → teman cukup bayar <b>{rupiah(Math.max(0, 50000 - ref.discount))}</b>, kamu dapat {rupiah(ref.fee)}.</p>
            <p className="text-sm text-emerald-800 mt-1 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">💰 Bonus penjualan: setiap teman yang kamu ajak <b>belanja</b>, kamu dapat <b data-testid="referral-comm-pct">{ref.commission_pct}%</b> dari ongkir tiap pesanannya. Persen naik makin banyak teman yang diajak!</p>
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <div className="flex-1 bg-white rounded-xl border border-stone-200 p-3">
                <p className="text-xs text-stone-500">Kode referralmu</p>
                <p className="font-mono font-bold text-xl text-stone-900 tracking-wider" data-testid="referral-code">{ref.referral_code}</p>
              </div>
              <button data-testid="referral-copy" onClick={copyLink} className="bg-stone-900 hover:bg-stone-800 text-white text-sm font-semibold px-4 rounded-xl flex items-center justify-center gap-2 py-3"><Copy size={16} />Salin Link Ajak</button>
            </div>
          </div>

          {ref.badge && (() => {
            const bs = BADGE_STYLE[ref.badge.tier] || BADGE_STYLE.none;
            return (
              <div data-testid="referral-badge" className={`rounded-2xl border-2 ${bs.ring} ${bs.bg} p-5 flex items-center gap-4`}>
                <div className={`w-16 h-16 rounded-full bg-white border-2 ${bs.ring} flex items-center justify-center shrink-0`}>
                  <Award className={bs.icon} size={32} />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-stone-500">Badge Duta Kedai Impor</p>
                  <p className={`font-display font-extrabold text-xl ${bs.text}`} data-testid="badge-name">{ref.badge.name}</p>
                  {ref.badge.next_name ? (
                    <p className="text-xs text-stone-500 mt-1" data-testid="badge-progress">Ajak <b>{ref.badge.next_at - ref.badge.count}</b> teman lagi untuk naik ke <b>{ref.badge.next_name}</b></p>
                  ) : (
                    <p className="text-xs text-emerald-700 mt-1 font-semibold">Level tertinggi tercapai! 🎉</p>
                  )}
                </div>
              </div>
            );
          })()}

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-white border border-stone-200 rounded-2xl p-5">
              <p className="text-xs text-stone-500 flex items-center gap-1.5"><Wallet size={14} />Saldo Referral</p>
              <p className="text-3xl font-display font-extrabold text-emerald-700 mt-1" data-testid="referral-balance">{rupiah(ref.balance)}</p>
              <p className="text-xs text-stone-400 mt-1">Minimum penarikan {rupiah(ref.min_withdrawal)}.</p>
            </div>
            <div className="bg-white border border-stone-200 rounded-2xl p-5">
              <p className="text-xs text-stone-500 flex items-center gap-1.5"><Users size={14} />Teman Berhasil Diajak</p>
              <p className="text-3xl font-display font-extrabold text-stone-900 mt-1" data-testid="referral-count">{ref.history.length}</p>
              <p className="text-xs text-stone-400 mt-1">Dihitung saat membership temanmu disetujui.</p>
            </div>
          </div>

          <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="commission-summary">
            <p className="text-xs text-stone-500 flex items-center gap-1.5"><Wallet size={14} />Total Komisi Penjualan Diterima</p>
            <p className="text-3xl font-display font-extrabold text-amber-600 mt-1" data-testid="commission-total">{rupiah(ref.commission_total || 0)}</p>
            <p className="text-xs text-stone-400 mt-1">Komisi <b>{ref.commission_pct}%</b> dari ongkir tiap pesanan teman yang diajak. Teman harus transaksi minimal 1× per {ref.commission_active_days || 30} hari agar komisi tetap aktif.</p>
          </div>

          {/* Leaderboard Top Duta bulan ini */}
          {board && board.leaderboard.length > 0 && (
            <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="referral-leaderboard">
              <div className="flex items-center justify-between mb-3">
                <p className="font-semibold text-stone-800 flex items-center gap-2"><Trophy size={18} className="text-amber-500" />Top Duta Bulan Ini</p>
                {board.my_rank && <span className="text-xs text-stone-500">Peringkatmu: <b className="text-emerald-700" data-testid="leaderboard-myrank">#{board.my_rank}</b></span>}
              </div>
              <div className="space-y-1.5">
                {board.leaderboard.map((b) => (
                  <div key={b.rank} data-testid={`leaderboard-row-${b.rank}`} className={`flex items-center gap-3 rounded-xl px-3 py-2 ${b.is_me ? "bg-emerald-50 border border-emerald-200" : "bg-stone-50"}`}>
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${b.rank === 1 ? "bg-amber-100 text-amber-700" : b.rank === 2 ? "bg-slate-100 text-slate-600" : b.rank === 3 ? "bg-orange-100 text-orange-700" : "bg-stone-100 text-stone-500"}`}>{b.rank}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-stone-800 truncate">{b.name}{b.is_me && <span className="text-emerald-600 text-xs font-semibold"> (kamu)</span>}</p>
                      <p className="text-[11px] text-stone-400">{b.badge}</p>
                    </div>
                    <span className="text-sm font-semibold text-stone-700 shrink-0">{b.count} <span className="text-xs text-stone-400 font-normal">teman</span></span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-stone-400 mt-3">Semakin banyak teman yang jadi member, makin tinggi peringkatmu. Reset tiap awal bulan.</p>
            </div>
          )}

          {/* Withdraw form */}
          <div className="bg-white border border-stone-200 rounded-2xl p-5 space-y-3" data-testid="withdraw-form">
            <p className="font-semibold text-stone-800 flex items-center gap-2"><Send size={16} className="text-emerald-600" />Tarik Saldo</p>
            <p className="text-xs text-stone-500">Penarikan diproses maksimal <b>2×24 jam</b> (tidak termasuk hari Minggu).</p>
            <div className="grid sm:grid-cols-2 gap-2">
              <input data-testid="wd-amount" type="number" value={wd.amount} onChange={(e) => setWd({ ...wd, amount: e.target.value })} placeholder={`Jumlah (min ${rupiah(ref.min_withdrawal)})`} className="border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400" />
              <input data-testid="wd-bank" value={wd.bank_name} onChange={(e) => setWd({ ...wd, bank_name: e.target.value })} placeholder="Nama Bank / e-wallet" className="border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400" />
              <input data-testid="wd-account" value={wd.bank_account} onChange={(e) => setWd({ ...wd, bank_account: e.target.value })} placeholder="No. Rekening / HP" className="border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400" />
              <input data-testid="wd-owner" value={wd.bank_owner} onChange={(e) => setWd({ ...wd, bank_owner: e.target.value })} placeholder="Atas Nama" className="border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400" />
            </div>
            <button data-testid="wd-submit" onClick={submitWithdraw} disabled={wdSaving || ref.balance < ref.min_withdrawal} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50">
              {wdSaving && <Loader2 className="animate-spin" size={16} />}Ajukan Penarikan
            </button>
            {ref.balance < ref.min_withdrawal && <p className="text-xs text-stone-400 text-center">Saldo belum mencapai minimum penarikan.</p>}
          </div>

          {/* Withdrawal history */}
          {ref.withdrawals.length > 0 && (
            <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="withdraw-history">
              <p className="font-semibold text-stone-800 mb-3">Riwayat Penarikan</p>
              <div className="space-y-2">
                {ref.withdrawals.map((w) => (
                  <div key={w.id} data-testid={`wd-item-${w.id}`} className="border border-stone-100 rounded-xl p-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-stone-800">{rupiah(w.amount)}</span>
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${w.status === "COMPLETED" ? "bg-emerald-100 text-emerald-700" : w.status === "REJECTED" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{WD_LABEL[w.status]}</span>
                    </div>
                    <p className="text-xs text-stone-400 mt-0.5">{w.bank_name} · {w.bank_account} · {w.bank_owner}</p>
                    {w.status === "REJECTED" && w.reject_reason && <p className="text-xs text-red-600 mt-1">Alasan: {w.reject_reason} (saldo dikembalikan)</p>}
                    {w.status === "COMPLETED" && w.proof_photo && (
                      <div className="mt-2">
                        <p className="text-[11px] text-stone-500 mb-1">Bukti transfer dari admin:</p>
                        <AuthImage path={w.proof_photo} className="w-32 rounded-lg border border-stone-200" data-testid={`wd-proof-${w.id}`} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Commission history */}
          {ref.commission_history && ref.commission_history.length > 0 && (
            <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="commission-history">
              <p className="font-semibold text-stone-800 mb-3">Riwayat Komisi Penjualan</p>
              <div className="space-y-2">
                {ref.commission_history.map((c, i) => (
                  <div key={i} data-testid={`commission-item-${i}`} className="flex items-center justify-between border-b border-stone-50 last:border-0 py-2 text-sm">
                    <div>
                      <p className="font-medium text-stone-800">{c.referee_name} <span className="text-stone-400 text-xs">· {c.referee_email}</span></p>
                      <p className="text-xs text-stone-400">{c.pct}% × ongkir {rupiah(c.shipping_fee)} · {new Date(c.created_at).toLocaleDateString("id-ID")}</p>
                    </div>
                    <span className="text-amber-600 font-semibold text-sm">+{rupiah(c.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Invite history / audit */}
          {ref.history.length > 0 && (
            <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="referral-history">
              <p className="font-semibold text-stone-800 mb-3">Riwayat Ajak Teman</p>
              <div className="space-y-2">
                {ref.history.map((h, i) => (
                  <div key={i} data-testid={`referral-item-${i}`} className="flex items-center justify-between border-b border-stone-50 last:border-0 py-2 text-sm">
                    <div>
                      <p className="font-medium text-stone-800">{h.referee_name} <span className="text-stone-400 text-xs">· {h.referee_email}</span></p>
                      <p className="text-xs text-stone-400">{h.plan} · {new Date(h.created_at).toLocaleDateString("id-ID")}</p>
                    </div>
                    <span className="text-emerald-700 font-semibold text-sm">+{rupiah(h.fee)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
